import React, { useEffect, useState } from 'react';
import NumberFormat from 'react-number-format';
import { useHistory } from 'react-router-dom';
import { Button, Dimmer, Form, Icon, Loader, Table } from 'semantic-ui-react';
import store from '../../../store';
import { formatDate, formatDateTime } from '../../../utils/shared.utils';
import '../reports/order.report.scss';

interface HistoryRecord {
    id: number;
    productId: number;
    deliveryCost: number;
    createdBy: string;
    createdAt: string;
}

function DeliveryCostHistoryPage(props: any) {
    const history = useHistory();

    useEffect(() => {
        var token = localStorage.getItem('adminToken');
        if (token && token !== 'undefined') {
            getHistory(props.match.params.id);
        } else {
            console.log("Will redirect to: " + window.location.pathname);
            store.dispatch({ type: "redirectUrl", data: window.location.pathname });
            history.push("/");
        }
    }, []);

    const [productName, setProductName] = useState("");
    const [slug, setSlug] = useState("");
    const [allHistoryRecords, setAllHistoryRecords] = useState<HistoryRecord[]>([]);
    const [filteredHistoryRecords, setFilteredHistoryRecords] = useState<HistoryRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    const tomorrow = new Date();
    const pastDate = new Date();
    tomorrow.setDate(tomorrow.getDate());
    pastDate.setDate(pastDate.getDate() - 30); // Default to 30 days ago
    const [startDate, setStartDate] = useState(formatDate(pastDate));
    const [endDate, setEndDate] = useState(formatDate(tomorrow));

    const getHistory = (productId: number) => {
        var token = localStorage.getItem('adminToken');
        if (!token || token === 'undefined') {
            console.log("Will redirect to: " + window.location.pathname);
            store.dispatch({ type: "redirectUrl", data: window.location.pathname });
            history.push("/mongol/login");
            return;
        }
        setLoading(true);
        setErrorMsg('');

        fetch(`${process.env.REACT_APP_API_URL}product/delivery-cost-history/${productId}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': token,
                'Content-Type': 'application/json'
            }
        })
            .then(res => {
                setLoading(false);
                if (res.status === 401) {
                    setErrorMsg('Эрх хүрэлцэхгүй байна');
                } else if (res.ok) {
                    res.json().then(data => {
                        if (data && data.data) {
                            const records = data.data;
                            if (records.length > 0) {
                                setProductName(records[0].productName || '');
                                setSlug(records[0].slug || '');
                            }
                            setAllHistoryRecords(records);
                            filterRecordsByDate(records, startDate, endDate);
                        }
                    });
                } else {
                    setErrorMsg('Түүх татахад алдаа гарлаа');
                }
            })
            .catch(err => {
                setLoading(false);
                setErrorMsg('Сүлжээний алдаа');
                console.error(err);
            });
    };

    const filterRecordsByDate = (records: HistoryRecord[], start: string, end: string) => {
        const startDateTime = new Date(start + " 00:00:00").getTime();
        const endDateTime = new Date(end + " 23:59:59").getTime();
        
        const filtered = records.filter(record => {
            const recordTime = new Date(record.createdAt).getTime();
            return recordTime >= startDateTime && recordTime <= endDateTime;
        });
        
        setFilteredHistoryRecords(filtered);
    };

    const onFilterApply = () => {
        filterRecordsByDate(allHistoryRecords, startDate, endDate);
    };

    const formatDateDisplay = (dateString: string) => {
        return formatDateTime(new Date(dateString));
    };

    return (
        <div className="page-admin">
            <Dimmer active={loading}>
                <Loader size='medium'>Түүх татаж байна...</Loader>
            </Dimmer>

            <div className="flex-div width-100">
                <section className="admin-warehouse-form" id="my-form">
                    <div className="warehouse-actions">
                        <a onClick={() => history.goBack()}>
                            <Icon name="angle left"/>
                            <span className="underline">Буцах</span>
                        </a>
                        <div className="width-100 text-center">
                            <h1>Хүргэлтийн зардлын өөрчлөлтийн түүх</h1>
                        </div>
                        <div className="width-100"><h2>{productName}</h2></div>
                        {slug ? (
                            <a className="underline" href={"/product/" + slug} target="_blank">
                                Худалдааны линк
                            </a>
                        ) : null}
                        <Form className="report-filter flex-div margin-top-50">
                            <Form.Input
                                label="Эхний өдөр: "
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                            <Form.Input
                                label="Сүүлийн өдөр: "
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                            <Form.Field className="report-filter-actions">
                                <label>&nbsp;</label>
                                <Button type="button" onClick={onFilterApply}>
                                    Хайх
                                </Button>
                            </Form.Field>
                            <Form.Field className="report-filter-actions">
                                <label>&nbsp;</label>
                                <Button type="button" onClick={() => getHistory(Number(props.match.params.id))}>
                                    <Icon name="refresh" />
                                    Шинэчлэх
                                </Button>
                            </Form.Field>
                        </Form>

                        {errorMsg && (
                            <div className="error-msg" style={{ 
                                marginBottom: '20px', 
                                padding: '10px', 
                                backgroundColor: '#fff6f6', 
                                border: '1px solid #e0b4b4', 
                                borderRadius: '4px' 
                            }}>
                                <Icon name="warning circle" color="red" />
                                {errorMsg}
                            </div>
                        )}

                        {!loading && filteredHistoryRecords.length === 0 && !errorMsg && (
                            <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                                <Icon name="inbox" size="huge" style={{ opacity: 0.3 }} />
                                <p>Хүргэлтийн зардлын өөрчлөлт байхгүй байна</p>
                            </div>
                        )}

                        {!loading && filteredHistoryRecords.length > 0 && (
                            <>
                                <Table striped className="admin-list-table" width={16}>
                                    <Table.Header>
                                        <Table.Row>
                                            <Table.HeaderCell width={2}>№</Table.HeaderCell>
                                            <Table.HeaderCell width={4}>Хүргэлтийн зардал</Table.HeaderCell>
                                            <Table.HeaderCell width={5}>Өөрчилсөн хэрэглэгч</Table.HeaderCell>
                                            <Table.HeaderCell width={5}>Огноо</Table.HeaderCell>
                                        </Table.Row>
                                    </Table.Header>
                                    <Table.Body>
                                        {filteredHistoryRecords.map((record, index) => (
                                            <Table.Row key={record.id}>
                                                <Table.Cell data-label="№">{index + 1}</Table.Cell>
                                                <Table.Cell data-label="Хүргэлтийн зардал">
                                                    <strong>
                                                        <NumberFormat 
                                                            displayType="text" 
                                                            value={record.deliveryCost} 
                                                            suffix="₮" 
                                                            thousandSeparator={true} 
                                                        />
                                                    </strong>
                                                </Table.Cell>
                                                <Table.Cell data-label="Өөрчилсөн хэрэглэгч">
                                                    <Icon name="user" />
                                                    {record.createdBy}
                                                </Table.Cell>
                                                <Table.Cell data-label="Огноо">
                                                    <Icon name="clock outline" />
                                                    {formatDateDisplay(record.createdAt)}
                                                </Table.Cell>
                                            </Table.Row>
                                        ))}
                                    </Table.Body>
                                </Table>
                                <div style={{ marginTop: '20px', color: '#666', fontSize: '0.9em' }}>
                                    <Icon name="info circle" />
                                    Нийт {filteredHistoryRecords.length} өөрчлөлт бүртгэгдсэн байна
                                    {allHistoryRecords.length !== filteredHistoryRecords.length && (
                                        <span> (Бүгдээрээ {allHistoryRecords.length} өөрчлөлт байна)</span>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </section>
            </div>
        </div>
    );
}

export default DeliveryCostHistoryPage;
